/** Baseline time for “must re-verify this app session” (cleared when WebView/session ends). */
const SESSION_VERIFY_BASELINE_KEY = 'wortelogy-pos-session-verify-since'
/** Bumps when Firebase auth becomes active for a user (login or new uid); survives sessionStorage. */
const AUTH_TRUST_EPOCH_KEY = 'wortelogy-pos-auth-trust-epoch'

function storageKeyPrinterFails(placeId: string): string {
  return `wortelogy-printer-fail::${placeId.trim()}`
}

export const PRINTER_FAIL_CHANGE_EVENT = 'wortelogy-printer-fail-change'

export type PrinterFailChangeDetail = { deviceId: string; failed: boolean }

/** Monotonic ms for this browser session; set on first read per session tab/WebView. */
export function getPosSessionVerifyBaselineMs(): number {
  if (typeof window === 'undefined') return Date.now()
  try {
    const raw = sessionStorage.getItem(SESSION_VERIFY_BASELINE_KEY)
    if (raw) {
      const t = Number(raw)
      if (Number.isFinite(t)) return t
    }
    const now = Date.now()
    sessionStorage.setItem(SESSION_VERIFY_BASELINE_KEY, String(now))
    return now
  } catch {
    return Date.now()
  }
}

/** Last auth-scoped trust floor (ms); 0 if never set. */
export function getAuthTrustEpochMs(): number {
  if (typeof window === 'undefined') return 0
  try {
    const raw = localStorage.getItem(AUTH_TRUST_EPOCH_KEY)
    if (!raw?.trim()) return 0
    const t = Number(raw)
    return Number.isFinite(t) ? t : 0
  } catch {
    return 0
  }
}

/**
 * Set a new trust floor when a user session starts or changes uid.
 * Does not bump on token refresh for the same uid, or on sign-out (`nextUid` null).
 */
export function bumpAuthTrustEpochIfNeeded(prevUid: string | null, nextUid: string | null): number {
  if (!nextUid) {
    return getAuthTrustEpochMs()
  }
  if (prevUid !== null && prevUid === nextUid) {
    return getAuthTrustEpochMs()
  }
  const now = Date.now()
  if (typeof window === 'undefined') return now
  try {
    localStorage.setItem(AUTH_TRUST_EPOCH_KEY, String(now))
  } catch {
    /* ignore */
  }
  return now
}

export function loadPersistedPrinterFails(placeId: string | undefined): Record<string, true> {
  if (!placeId?.trim() || typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(storageKeyPrinterFails(placeId))
    if (!raw?.trim()) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Record<string, true> = {}
    for (const [id, ok] of Object.entries(parsed)) {
      const key = id?.trim()
      if (!key || ok !== true) continue
      out[key] = true
    }
    return out
  } catch {
    return {}
  }
}

export function setPersistedPrinterFail(placeId: string | undefined, deviceId: string, failed: boolean): void {
  const pid = placeId?.trim()
  const did = deviceId?.trim()
  if (!pid || !did || typeof window === 'undefined') return
  try {
    const key = storageKeyPrinterFails(pid)
    const raw = localStorage.getItem(key)
    let map: Record<string, boolean> = {}
    if (raw?.trim()) {
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          for (const [id, v] of Object.entries(parsed)) {
            if (id?.trim() && v === true) map[id.trim()] = true
          }
        }
      } catch {
        /* reset */
      }
    }
    if (failed) {
      map[did] = true
    } else {
      delete map[did]
    }
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(map))
    }
    window.dispatchEvent(
      new CustomEvent<PrinterFailChangeDetail>(PRINTER_FAIL_CHANGE_EVENT, {
        detail: { deviceId: did, failed },
      }),
    )
  } catch {
    /* ignore */
  }
}
